export type BoardUnit = {
  readonly unitId: string;
  readonly floorId: string;
  readonly available: boolean;
  readonly conflicts: readonly { readonly kind: 'booking' | 'maintenance'; readonly bookingId?: string | null }[];
};

export type BoardTone = 'available' | 'reserved' | 'maintenance';

export function availabilityTone(unit: BoardUnit): BoardTone {
  if (unit.conflicts.some((conflict) => conflict.kind === 'maintenance')) return 'maintenance';
  if (!unit.available || unit.conflicts.some((conflict) => conflict.kind === 'booking')) return 'reserved';
  return 'available';
}

export function groupByFloor<T extends { readonly floorId: string }>(
  units: readonly T[],
  floors: readonly { readonly id: string; readonly label: string }[],
): { readonly floorId: string; readonly label: string; readonly units: readonly T[] }[] {
  const labels = new Map(floors.map((floor) => [floor.id, floor.label]));
  const grouped = new Map<string, T[]>();
  for (const unit of units) {
    const rows = grouped.get(unit.floorId) ?? [];
    rows.push(unit);
    grouped.set(unit.floorId, rows);
  }
  const known = floors.map((floor) => floor.id).filter((id) => grouped.has(id));
  const extra = [...grouped.keys()].filter((id) => !known.includes(id));
  return [...known, ...extra].map((floorId) => ({
    floorId,
    label: labels.get(floorId) ?? floorId,
    units: grouped.get(floorId) ?? [],
  }));
}

// The four unit states of the approved board design. Physical occupancy and
// the blockers come from the server (getUnitAvailability); this only names
// them. Occupied = physically occupied now (today view) or held by a stay
// that has checked in and not out (other dates). Reserved = held by a
// Booking that is not in-house. Maintenance always wins: it blocks bookings.
export type UnitState = 'available' | 'reserved' | 'occupied' | 'maintenance';

export const UNIT_STATES: readonly UnitState[] = ['available', 'reserved', 'occupied', 'maintenance'];

export function unitState(
  unit: BoardUnit & { readonly physicalOccupied?: boolean },
  options: { readonly today: boolean; readonly inHouse?: (bookingId: string) => boolean } = { today: true },
): UnitState {
  if (unit.conflicts.some((conflict) => conflict.kind === 'maintenance')) return 'maintenance';
  if (options.today && unit.physicalOccupied) return 'occupied';
  const bookings = unit.conflicts.filter((conflict) => conflict.kind === 'booking');
  if (
    !options.today &&
    options.inHouse &&
    bookings.some((conflict) => Boolean(conflict.bookingId && options.inHouse?.(conflict.bookingId)))
  ) {
    return 'occupied';
  }
  if (!unit.available || bookings.length > 0) return 'reserved';
  return 'available';
}
